<script setup lang="ts">
import { computed } from 'vue';

export type VisualType = 'codegen' | 'checklist' | 'scaffold' | 'review' | 'integrate';

export interface ShowcaseItem {
    title: string;
    subtitle?: string;
    visual: VisualType;
}

interface ScaffoldRow {
    icon: string;
    label: string;
    active?: boolean;
    dim?: boolean;
}

const props = withDefaults(
    defineProps<{
        item: ShowcaseItem;
        large?: boolean;
    }>(),
    {
        large: false,
    }
);

/* ─── Static Data Definitions ─── */

const cgNodes = [
    'left-[32%] top-[36.8%]',
    'left-[32%] top-[46.3%]',
    'left-[68%] top-[36.8%]',
    'left-[68%] top-[46.3%]',
] as const;

/* Faint "tech HUD" data labels drifting around the code-generation circuit */
const cgHudTicks = [
    { text: 'AI', pos: 'left-[9%] top-[13%]', cls: 'hud-tick--ai text-[2.4cqw] font-semibold' },
    { text: '0 1 0 1 0 1 1 0', pos: 'left-[6%] top-[30%]', cls: 'text-[1.3cqw]' },
    { text: 'AI', pos: 'right-[9%] top-[16%]', cls: 'hud-tick--ai text-[2.4cqw] font-semibold' },
    { text: '1 0 1 1 0 0 1 1', pos: 'right-[7%] top-[34%]', cls: 'text-[1.3cqw]' },
    { text: 'AI', pos: 'right-[15%] top-[64%]', cls: 'hud-tick--ai text-[2cqw] font-semibold' },
    { text: '0 0 1 1 0 1', pos: 'left-[20%] top-[74%]', cls: 'text-[1.2cqw]' },
] as const;

const scaffoldRows: ScaffoldRow[] = [
    { icon: 'streamline:user-check-validate', label: 'Close', active: true },
    { icon: 'streamline:flash-1', label: 'Closed' },
    { icon: 'streamline:database', label: 'Content' },
    { icon: 'streamline:cloud-wifi', label: 'CoreWindow', dim: true },
];

/* Tool Icons placed on the upper part of the outer (1st) arc */
const reviewIcons = [
    { icon: 'streamline:bracket', pos: 'left-[20.5%] top-[20%]' },
    { icon: 'streamline:interface-search-glass-search-magnifying', pos: 'left-[17%] top-[55%]' },
] as const;

const reviewDots = [
    'left-[33%] top-[21%]',
    'left-[24.5%] top-[42.1%]',
    'left-[33%] top-[65.5%]',
] as const;

/* Agentic tools & model providers the platform runs */
const agentIcons = [
    {
        label: 'Claude Code',
        d: 'M21 10.5h3v3h-3v3h-1.5v3H18v-3h-1.5v3H15v-3H9v3H7.5v-3H6v3H4.5v-3H3v-3H0v-3h3v-6h18Zm-15 0h1.5v-3H6Zm10.5 0H18v-3h-1.5z',
    },
    {
        label: 'GitHub Copilot',
        d: 'M23.922 16.997C23.061 18.492 18.063 22.02 12 22.02 5.937 22.02.939 18.492.078 16.997A.641.641 0 0 1 0 16.741v-2.869a.883.883 0 0 1 .053-.22c.372-.935 1.347-2.292 2.605-2.656.167-.429.414-1.055.644-1.517a10.098 10.098 0 0 1-.052-1.086c0-1.331.282-2.499 1.132-3.368.397-.406.89-.717 1.474-.952C7.255 2.937 9.248 1.98 11.978 1.98c2.731 0 4.767.957 6.166 2.093.584.235 1.077.546 1.474.952.85.869 1.132 2.037 1.132 3.368 0 .368-.014.733-.052 1.086.23.462.477 1.088.644 1.517 1.258.364 2.233 1.721 2.605 2.656a.841.841 0 0 1 .053.22v2.869a.641.641 0 0 1-.078.256Zm-11.75-5.992h-.344a4.359 4.359 0 0 1-.355.508c-.77.947-1.918 1.492-3.508 1.492-1.725 0-2.989-.359-3.782-1.259a2.137 2.137 0 0 1-.085-.104L4 11.746v6.585c1.435.779 4.514 2.179 8 2.179 3.486 0 6.565-1.4 8-2.179v-6.585l-.098-.104s-.033.045-.085.104c-.793.9-2.057 1.259-3.782 1.259-1.59 0-2.738-.545-3.508-1.492a4.359 4.359 0 0 1-.355-.508Zm2.328 3.25c.549 0 1 .451 1 1v2c0 .549-.451 1-1 1-.549 0-1-.451-1-1v-2c0-.549.451-1 1-1Zm-5 0c.549 0 1 .451 1 1v2c0 .549-.451 1-1 1-.549 0-1-.451-1-1v-2c0-.549.451-1 1-1Zm3.313-6.185c.136 1.057.403 1.913.878 2.497.442.544 1.134.938 2.344.938 1.573 0 2.292-.337 2.657-.751.384-.435.558-1.15.558-2.361 0-1.14-.243-1.847-.705-2.319-.477-.488-1.319-.862-2.824-1.025-1.487-.161-2.192.138-2.533.529-.269.307-.437.808-.438 1.578v.021c0 .265.021.562.063.893Zm-1.626 0c.042-.331.063-.628.063-.894v-.02c-.001-.77-.169-1.271-.438-1.578-.341-.391-1.046-.69-2.533-.529-1.505.163-2.347.537-2.824 1.025-.462.472-.705 1.179-.705 2.319 0 1.211.175 1.926.558 2.361.365.414 1.084.751 2.657.751 1.21 0 1.902-.394 2.344-.938.475-.584.742-1.44.878-2.497Z',
    },
    {
        label: 'Cursor',
        d: 'M11.503.131 1.891 5.678a.84.84 0 0 0-.42.726v11.188c0 .3.162.575.42.724l9.609 5.55a1 1 0 0 0 .998 0l9.61-5.55a.84.84 0 0 0 .42-.724V6.404a.84.84 0 0 0-.42-.726L12.497.131a1.01 1.01 0 0 0-.996 0M2.657 6.338h18.55c.263 0 .43.287.297.515L12.23 22.918c-.062.107-.229.064-.229-.06V12.335a.59.59 0 0 0-.295-.51l-9.11-5.257c-.109-.063-.064-.23.061-.23',
    },
    {
        label: 'Windsurf',
        d: 'M23.55 5.067c-1.2038-.002-2.1806.973-2.1806 2.1765v4.8676c0 .972-.8035 1.7594-1.7597 1.7594-.568 0-1.1352-.286-1.4718-.7659l-4.9713-7.1003c-.4125-.5896-1.0837-.941-1.8103-.941-1.1334 0-2.1533.9635-2.1533 2.153v4.8957c0 .972-.7969 1.7594-1.7596 1.7594-.57 0-1.1363-.286-1.4728-.7658L.4076 5.1598C.2822 4.9798 0 5.0688 0 5.2882v4.2452c0 .2147.0656.4228.1884.599l5.4748 7.8183c.3234.462.8006.8052 1.3509.9298 1.3771.313 2.6446-.747 2.6446-2.0977v-4.893c0-.972.7875-1.7593 1.7596-1.7593h.003a1.798 1.798 0 0 1 1.4718.7658l4.9723 7.0994c.4135.5905 1.05.941 1.8093.941 1.1587 0 2.1515-.9645 2.1515-2.153v-4.8948c0-.972.7875-1.7594 1.7596-1.7594h.194a.22.22 0 0 0 .2204-.2202v-4.622a.22.22 0 0 0-.2203-.2203Z',
    },
    {
        label: 'Cline',
        d: 'm23.365 13.556-1.442-2.895V8.994c0-2.764-2.218-5.002-4.954-5.002h-2.464c.178-.367.276-.779.276-1.213A2.77 2.77 0 0 0 12.018 0a2.77 2.77 0 0 0-2.763 2.779c0 .434.098.846.276 1.213H7.067c-2.736 0-4.954 2.238-4.954 5.002v1.667L.64 13.549c-.149.29-.149.636 0 .927l1.472 2.855v1.667C2.113 21.762 4.33 24 7.067 24h9.902c2.736 0 4.954-2.238 4.954-5.002V17.33l1.44-2.865c.143-.286.143-.622.002-.91m-12.854 2.36a2.27 2.27 0 0 1-2.261 2.273 2.27 2.27 0 0 1-2.261-2.273v-4.042A2.27 2.27 0 0 1 8.249 9.6a2.267 2.267 0 0 1 2.262 2.274zm7.285 0a2.27 2.27 0 0 1-2.26 2.273 2.27 2.27 0 0 1-2.262-2.273v-4.042A2.267 2.267 0 0 1 15.535 9.6a2.267 2.267 0 0 1 2.261 2.274z',
    },
    {
        label: 'OpenCode',
        d: 'M22 24H2V0h20zM17 4.8H7v14.4h10z',
    },
    {
        label: 'Google Gemini',
        d: 'M11.04 19.32Q12 21.51 12 24q0-2.49.93-4.68.96-2.19 2.58-3.81t3.81-2.55Q21.51 12 24 12q-2.49 0-4.68-.93a12.3 12.3 0 0 1-3.81-2.58 12.3 12.3 0 0 1-2.58-3.81Q12 2.49 12 0q0 2.49-.96 4.68-.93 2.19-2.55 3.81a12.3 12.3 0 0 1-3.81 2.58Q2.49 12 0 12q2.49 0 4.68.96 2.19.93 3.81 2.55t2.55 3.81',
    },
    {
        label: 'DeepSeek',
        d: 'M23.748 4.651c-.254-.124-.364.113-.512.233-.051.04-.094.09-.137.137-.372.397-.806.657-1.373.626-.829-.046-1.537.214-2.163.848-.133-.782-.575-1.248-1.247-1.548-.352-.155-.708-.311-.955-.65-.172-.24-.219-.509-.305-.774-.055-.16-.11-.323-.293-.35-.2-.031-.278.136-.356.276-.313.572-.434 1.202-.422 1.84.027 1.436.633 2.58 1.838 3.393.137.094.172.187.129.323-.082.28-.18.553-.266.833-.055.179-.137.218-.328.14a5.5 5.5 0 0 1-1.737-1.179c-.857-.828-1.631-1.743-2.597-2.46a12 12 0 0 0-.689-.47c-.985-.957.13-1.743.387-1.836.27-.098.094-.433-.778-.428-.872.003-1.67.295-2.687.685a3 3 0 0 1-.465.136 9.6 9.6 0 0 0-2.883-.101c-1.885.21-3.39 1.1-4.497 2.622C.082 8.776-.231 10.854.152 13.02c.403 2.284 1.568 4.175 3.36 5.653 1.857 1.533 3.997 2.284 6.438 2.14 1.482-.085 3.132-.284 4.994-1.86.47.234.962.328 1.78.398.629.058 1.235-.031 1.705-.129.735-.155.684-.836.418-.961-2.155-1.004-1.682-.595-2.112-.926 1.095-1.295 2.768-3.598 3.284-6.733.05-.346.115-.834.108-1.114-.004-.171.035-.238.23-.257a4.2 4.2 0 0 0 1.545-.475c1.397-.763 1.96-2.016 2.093-3.517.02-.23-.004-.467-.247-.588M11.58 18.168c-2.088-1.642-3.101-2.183-3.52-2.16-.39.024-.32.472-.234.763.09.288.207.487.371.74.114.167.192.416-.113.603-.673.416-1.842-.14-1.897-.168-1.361-.801-2.5-1.86-3.301-3.306-.775-1.393-1.225-2.888-1.299-4.482-.02-.385.094-.522.477-.592a4.7 4.7 0 0 1 1.53-.038c2.131.311 3.946 1.264 5.467 2.774.868.86 1.525 1.887 2.202 2.89.72 1.066 1.494 2.082 2.48 2.915.348.291.626.513.892.677-.802.09-2.14.109-3.055-.615zm1.001-6.44a.306.306 0 0 1 .415-.287.3.3 0 0 1 .113.074.3.3 0 0 1 .086.214c0 .17-.136.307-.308.307a.303.303 0 0 1-.306-.307m3.11 1.596c-.2.081-.4.151-.591.16a1.25 1.25 0 0 1-.798-.254c-.274-.23-.47-.358-.551-.758a1.7 1.7 0 0 1 .015-.588c.07-.327-.007-.537-.238-.727-.188-.156-.426-.199-.689-.199a.6.6 0 0 1-.254-.078.253.253 0 0 1-.114-.358 1 1 0 0 1 .192-.21c.356-.202.767-.136 1.146.016.352.144.618.408 1.001.782.392.451.462.576.685.915.176.264.336.536.446.848.066.194-.02.353-.25.45',
    },
    {
        label: 'Mistral AI',
        d: 'M17.143 3.429v3.428h-3.429v3.429h-3.428V6.857H6.857V3.43H3.43v13.714H0v3.428h10.286v-3.428H6.857v-3.429h3.429v3.429h3.429v-3.429h3.428v3.429h-3.428v3.428H24v-3.428h-3.43V3.429z',
    },
    {
        label: 'Hugging Face',
        d: 'M12.025 1.13c-5.77 0-10.449 4.647-10.449 10.378 0 1.112.178 2.181.503 3.185.064-.222.203-.444.416-.577a.96.96 0 0 1 .524-.15c.293 0 .584.124.84.284.278.173.48.408.71.694.226.282.458.611.684.951v-.014c.017-.324.106-.622.264-.874s.403-.487.762-.543c.3-.047.596.06.787.203s.31.313.4.467c.15.257.212.468.233.542.01.026.653 1.552 1.657 2.54.616.605 1.01 1.223 1.082 1.912.055.537-.096 1.059-.38 1.572.637.121 1.294.187 1.967.187.657 0 1.298-.063 1.921-.178-.287-.517-.44-1.041-.384-1.581.07-.69.465-1.307 1.081-1.913 1.004-.987 1.647-2.513 1.657-2.539.021-.074.083-.285.233-.542.09-.154.208-.323.4-.467a1.08 1.08 0 0 1 .787-.203c.359.056.604.29.762.543s.247.55.265.874v.015c.225-.34.457-.67.683-.952.23-.286.432-.52.71-.694.257-.16.547-.284.84-.285a.97.97 0 0 1 .524.151c.228.143.373.388.43.625l.006.04a10.3 10.3 0 0 0 .534-3.273c0-5.731-4.678-10.378-10.449-10.378M8.327 6.583a1.5 1.5 0 0 1 .713.174 1.487 1.487 0 0 1 .617 2.013c-.183.343-.762-.214-1.102-.094-.38.134-.532.914-.917.71a1.487 1.487 0 0 1 .69-2.803m7.486 0a1.487 1.487 0 0 1 .689 2.803c-.385.204-.536-.576-.916-.71-.34-.12-.92.437-1.103.094a1.487 1.487 0 0 1 .617-2.013 1.5 1.5 0 0 1 .713-.174m-10.68 1.55a.96.96 0 1 1 0 1.921.96.96 0 0 1 0-1.92m13.838 0a.96.96 0 1 1 0 1.92.96.96 0 0 1 0-1.92M8.489 11.458c.588.01 1.965 1.157 3.572 1.164 1.607-.007 2.984-1.155 3.572-1.164.196-.003.305.12.305.454 0 .886-.424 2.328-1.563 3.202-.22-.756-1.396-1.366-1.63-1.32q-.011.001-.02.006l-.044.026-.01.008-.03.024q-.018.017-.035.036l-.032.04a1 1 0 0 0-.058.09l-.014.025q-.049.088-.11.19a1 1 0 0 1-.083.116 1.2 1.2 0 0 1-.173.18q-.035.029-.075.058a1.3 1.3 0 0 1-.251-.243 1 1 0 0 1-.076-.107c-.124-.193-.177-.363-.337-.444-.034-.016-.104-.008-.2.022q-.094.03-.216.087-.06.028-.125.063l-.13.074q-.067.04-.136.086a3 3 0 0 0-.135.096 3 3 0 0 0-.26.219 2 2 0 0 0-.12.121 2 2 0 0 0-.106.128l-.002.002a2 2 0 0 0-.09.132l-.001.001a1.2 1.2 0 0 0-.105.212q-.013.036-.024.073c-1.139-.875-1.563-2.317-1.563-3.203 0-.334.109-.457.305-.454m.836 10.354c.824-1.19.766-2.082-.365-3.194-1.13-1.112-1.789-2.738-1.789-2.738s-.246-.945-.806-.858-.97 1.499.202 2.362c1.173.864-.233 1.45-.685.64-.45-.812-1.683-2.896-2.322-3.295s-1.089-.175-.938.647 2.822 2.813 2.562 3.244-1.176-.506-1.176-.506-2.866-2.567-3.49-1.898.473 1.23 2.037 2.16c1.564.932 1.686 1.178 1.464 1.53s-3.675-2.511-4-1.297c-.323 1.214 3.524 1.567 3.287 2.405-.238.839-2.71-1.587-3.216-.642-.506.946 3.49 2.056 3.522 2.064 1.29.33 4.568 1.028 5.713-.624m5.349 0c-.824-1.19-.766-2.082.365-3.194 1.13-1.112 1.789-2.738 1.789-2.738s.246-.945.806-.858.97 1.499-.202 2.362c-1.173.864.233 1.45.685.64.451-.812 1.683-2.896 2.322-3.295s1.089-.175.938.647-2.822 2.813-2.562 3.244 1.176-.506 1.176-.506 2.866-2.567 3.49-1.898-.473 1.23-2.037 2.16c-1.564.932-1.686 1.178-1.464 1.53s3.675-2.511 4-1.297c.323 1.214-3.524 1.567-3.287 2.405.238.839 2.71-1.587 3.216-.642.506.946-3.49 2.056-3.522 2.064-1.29.33-4.568 1.028-5.713-.624',
    },
    {
        label: 'Ollama',
        d: 'M16.361 10.26a.894.894 0 0 0-.558.47l-.072.148.001.207c0 .193.004.217.059.353.076.193.152.312.291.448.24.238.51.3.872.205a.86.86 0 0 0 .517-.436.752.752 0 0 0 .08-.498c-.064-.453-.33-.782-.724-.897a1.06 1.06 0 0 0-.466 0zm-9.203.005c-.305.096-.533.32-.65.639a1.187 1.187 0 0 0-.06.52c.057.309.31.59.598.667.362.095.632.033.872-.205.14-.136.215-.255.291-.448.055-.136.059-.16.059-.353l.001-.207-.072-.148a.894.894 0 0 0-.565-.472 1.02 1.02 0 0 0-.474.007Zm4.184 2c-.131.071-.223.25-.195.383.031.143.157.288.353.407.105.063.112.072.117.136.004.038-.01.146-.029.243-.02.094-.036.194-.036.222.002.074.07.195.143.253.064.052.076.054.255.059.164.005.198.001.264-.03.169-.082.212-.234.15-.525-.052-.243-.042-.28.087-.355.137-.08.281-.219.324-.314a.365.365 0 0 0-.175-.48.394.394 0 0 0-.181-.033c-.126 0-.207.03-.355.124l-.085.053-.053-.032c-.219-.13-.259-.145-.391-.143a.396.396 0 0 0-.193.032zm.39-2.195c-.373.036-.475.05-.654.086-.291.06-.68.195-.951.328-.94.46-1.589 1.226-1.787 2.114-.04.176-.045.234-.045.53 0 .294.005.357.043.524.264 1.16 1.332 2.017 2.714 2.173.3.033 1.596.033 1.896 0 1.11-.125 2.064-.727 2.493-1.571.114-.226.169-.372.22-.602.039-.167.044-.23.044-.523 0-.297-.005-.355-.045-.531-.288-1.29-1.539-2.304-3.072-2.497a6.873 6.873 0 0 0-.855-.031zm.645.937a3.283 3.283 0 0 1 1.44.514c.223.148.537.458.671.662.166.251.26.508.303.82.02.143.01.251-.043.482-.08.345-.332.705-.672.957a3.115 3.115 0 0 1-.689.348c-.382.122-.632.144-1.525.138-.582-.006-.686-.01-.853-.042-.57-.107-1.022-.334-1.35-.68-.264-.28-.385-.535-.45-.946-.03-.192.025-.509.137-.776.136-.326.488-.73.836-.963.403-.269.934-.46 1.422-.512.187-.02.586-.02.773-.002zm-5.503-11a1.653 1.653 0 0 0-.683.298C5.617.74 5.173 1.666 4.985 2.819c-.07.436-.119 1.04-.119 1.503 0 .544.064 1.24.155 1.721.02.107.031.202.023.208a8.12 8.12 0 0 1-.187.152 5.324 5.324 0 0 0-.949 1.02 5.49 5.49 0 0 0-.94 2.339 6.625 6.625 0 0 0-.023 1.357c.091.78.325 1.438.727 2.04l.13.195-.037.064c-.269.452-.498 1.105-.605 1.732-.084.496-.095.629-.095 1.294 0 .67.009.803.088 1.266.095.555.288 1.143.503 1.534.071.128.243.393.264.407.007.003-.014.067-.046.141a7.405 7.405 0 0 0-.548 1.873c-.062.417-.071.552-.071.991 0 .56.031.832.148 1.279L3.42 24h1.478l-.05-.091c-.297-.552-.325-1.575-.068-2.597.117-.472.25-.819.498-1.296l.148-.29v-.177c0-.165-.003-.184-.057-.293a.915.915 0 0 0-.194-.25 1.74 1.74 0 0 1-.385-.543c-.424-.92-.506-2.286-.208-3.451.124-.486.329-.918.544-1.154a.787.787 0 0 0 .223-.531c0-.195-.07-.355-.224-.522a3.136 3.136 0 0 1-.817-1.729c-.14-.96.114-2.005.69-2.834.563-.814 1.353-1.336 2.237-1.475.199-.033.57-.028.776.01.226.04.367.028.512-.041.179-.085.268-.19.374-.431.093-.215.165-.333.36-.576.234-.29.46-.489.822-.729.413-.27.884-.467 1.352-.561.17-.035.25-.04.569-.04.319 0 .398.005.569.04a4.07 4.07 0 0 1 1.914.997c.117.109.398.457.488.602.034.057.095.177.132.267.105.241.195.346.374.43.14.068.286.082.503.045.343-.058.607-.053.943.016 1.144.23 2.14 1.173 2.581 2.437.385 1.108.276 2.267-.296 3.153-.097.15-.193.27-.333.419-.301.322-.301.722-.001 1.053.493.539.801 1.866.708 3.036-.062.772-.26 1.463-.533 1.854a2.096 2.096 0 0 1-.224.258.916.916 0 0 0-.194.25c-.054.109-.057.128-.057.293v.178l.148.29c.248.476.38.823.498 1.295.253 1.008.231 2.01-.059 2.581a.845.845 0 0 0-.044.098c0 .006.329.009.732.009h.73l.02-.074.036-.134c.019-.076.057-.3.088-.516.029-.217.029-1.016 0-1.258-.11-.875-.295-1.57-.597-2.226-.032-.074-.053-.138-.046-.141.008-.005.057-.074.108-.152.376-.569.607-1.284.724-2.228.031-.26.031-1.378 0-1.628-.083-.645-.182-1.082-.348-1.525a6.083 6.083 0 0 0-.329-.7l-.038-.064.131-.194c.402-.604.636-1.262.727-2.04a6.625 6.625 0 0 0-.024-1.358 5.512 5.512 0 0 0-.939-2.339 5.325 5.325 0 0 0-.95-1.02 8.097 8.097 0 0 1-.186-.152.692.692 0 0 1 .023-.208c.208-1.087.201-2.443-.017-3.503-.19-.924-.535-1.658-.98-2.082-.354-.338-.716-.482-1.15-.455-.996.059-1.8 1.205-2.116 3.01a6.805 6.805 0 0 0-.097.726c0 .036-.007.066-.015.066a.96.96 0 0 1-.149-.078A4.857 4.857 0 0 0 12 3.03c-.832 0-1.687.243-2.456.698a.958.958 0 0 1-.148.078c-.008 0-.015-.03-.015-.066a6.71 6.71 0 0 0-.097-.725C8.997 1.392 8.337.319 7.46.048a2.096 2.096 0 0 0-.585-.041Zm.293 1.402c.248.197.523.759.682 1.388.03.113.06.244.069.292.007.047.026.152.041.233.067.365.098.76.102 1.24l.002.475-.12.175-.118.178h-.278c-.324 0-.646.041-.954.124l-.238.06c-.033.007-.038-.003-.057-.144a8.438 8.438 0 0 1 .016-2.323c.124-.788.413-1.501.696-1.711.067-.05.079-.049.157.013zm9.825-.012c.17.126.358.46.498.888.28.854.36 2.028.212 3.145-.019.14-.024.151-.057.144l-.238-.06a3.693 3.693 0 0 0-.954-.124h-.278l-.119-.178-.119-.175.002-.474c.004-.669.066-1.19.214-1.772.157-.623.434-1.185.68-1.382.078-.062.09-.063.159-.012z',
    },
    {
        label: 'OpenRouter',
        d: 'M16.778 1.844v1.919q-.569-.026-1.138-.032-.708-.008-1.415.037c-1.93.126-4.023.728-6.149 2.237-2.911 2.066-2.731 1.95-4.14 2.75-.396.223-1.342.574-2.185.798-.841.225-1.753.333-1.751.333v4.229s.768.108 1.61.333c.842.224 1.789.575 2.185.799 1.41.798 1.228.683 4.14 2.75 2.126 1.509 4.22 2.11 6.148 2.236.88.058 1.716.041 2.555.005v1.918l7.222-4.168-7.222-4.17v2.176c-.86.038-1.611.065-2.278.021-1.364-.09-2.417-.357-3.979-1.465-2.244-1.593-2.866-2.027-3.68-2.508.889-.518 1.449-.906 3.822-2.59 1.56-1.109 2.614-1.377 3.978-1.466.667-.044 1.418-.017 2.278.02v2.176L24 6.014Z',
    },
    {
        label: 'Anthropic',
        d: 'M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z',
    },
    {
        label: 'Qwen',
        d: 'M23.919 14.545 20.817 9.17l1.47-2.544a.56.56 0 0 0 0-.566l-1.633-2.83a.57.57 0 0 0-.49-.283h-6.207L12.487.402a.57.57 0 0 0-.49-.284H8.732a.56.56 0 0 0-.49.284L5.139 5.775h-2.94a.56.56 0 0 0-.49.284L.077 8.887a.56.56 0 0 0 0 .567L3.18 14.83l-1.47 2.545a.56.56 0 0 0 0 .566l1.634 2.83a.57.57 0 0 0 .49.283h6.205l1.47 2.545a.57.57 0 0 0 .49.284h3.266a.57.57 0 0 0 .49-.284l3.104-5.375h2.94a.57.57 0 0 0 .49-.283l1.634-2.828a.55.55 0 0 0-.004-.568M8.733.686l1.634 2.828-1.634 2.828H21.8L20.164 9.17H7.425L5.63 6.06Zm1.306 19.801-6.205-.002 1.634-2.83h3.265L2.201 6.344h3.267q3.182 5.517 6.367 11.032zm10.124-5.66L18.53 12l-6.532 11.315-1.634-2.83c2.129-3.673 4.25-7.351 6.373-11.028h3.592l3.102 5.374z',
    },
    {
        label: 'Meta AI',
        d: 'M10.73.032c-1.333 0-2.032 1.016-2.032 2.285 0 2.223 2.127 4.953 4.318 4.953 1.301 0 2-.953 2-2.254 0-2.254-2.095-4.984-4.286-4.984m8.413 2.984c-1.968 0-3.397 2.73-3.397 4.825 0 1.556.794 3.016 2.254 3.016 2 0 3.365-2.762 3.365-4.794 0-1.523-.762-3.047-2.222-3.047M4.857 4.159c-1.968 0-3.778 1.016-3.778 2.54 0 1.65 2.16 2.793 4.254 2.793 1.873 0 3.778-.92 3.778-2.508 0-1.682-2.159-2.825-4.254-2.825m-.222 6.476C2.413 10.635 0 13.175 0 15.397c0 1.301.825 2.159 2.095 2.159 2.19 0 4.603-2.508 4.603-4.794 0-1.27-.761-2.127-2.063-2.127m16.667.698c-2.223 0-5.016 1.62-5.016 3.651 0 1.238 1.047 2.19 2.698 2.19 2.159 0 5.016-1.587 5.016-3.682 0-1.302-1.08-2.159-2.698-2.159M7.619 16c-1.524 0-2.38 1.746-2.38 3.429 0 2.063 1.269 4.54 3.047 4.54 1.524 0 2.38-1.81 2.38-3.461C10.667 18.57 9.46 16 7.62 16m6.571 1.016c-1.333 0-2.476.667-2.476 2.032 0 2.095 2.667 4.063 4.92 4.063 1.366 0 2.54-.73 2.54-2.063 0-2.032-2.698-4.032-4.984-4.032',
    },
    {
        label: 'DeepMind',
        d: 'm5.99,1.62a8.54,8.54 0 0 0 -2.54,6.83c0.35,4.4 4.51,7.99 8.28,7.99c3.5,0 4.88,-3.06 4.54,-5.14a4.32,4.32 0 0 0 -0.95,-2.07c0.63,0.34 1.24,0.77 1.81,1.3c1.52,1.41 2.44,3.23 2.58,5.1c0.33,4.13 -2.73,8.37 -7.85,8.37c-1.69,0 -3.48,-0.43 -4.98,-1.14c-4.06,-1.92 -6.88,-6.06 -6.88,-10.86c0,-4.43 2.41,-8.3 5.99,-10.38zm6.15,-1.62c1.69,0 3.48,0.43 4.98,1.14a12,12 0 0 1 6.88,10.86c0,4.43 -2.41,8.3 -5.99,10.38a8.54,8.54 0 0 0 2.54,-6.83c-0.35,-4.4 -4.51,-7.99 -8.28,-7.99c-3.5,0 -4.88,3.06 -4.54,5.14a4.3,4.3 0 0 0 0.96,2.07a8.72,8.72 0 0 1 -1.81,-1.3c-1.52,-1.41 -2.44,-3.23 -2.59,-5.1c-0.33,-4.13 2.73,-8.37 7.85,-8.37z',
    },
    {
        label: 'LangChain',
        d: 'M13.796 0a6.93 6.93 0 0 0-4.91 2.019L5.451 5.455l3.273 3.27 3.432-3.432a2.284 2.284 0 0 1 3.277 0 2.28 2.28 0 0 1 0 3.275L12 12.001l3.273 3.273 3.433-3.435c2.692-2.692 2.692-7.127 0-9.82A6.92 6.92 0 0 0 13.796 0m-5.07 8.728-3.433 3.434c-2.692 2.693-2.692 7.126 0 9.819A6.92 6.92 0 0 0 10.203 24a6.93 6.93 0 0 0 4.911-2.02l3.432-3.432-3.271-3.272-3.433 3.433a2.284 2.284 0 0 1-3.277 0 2.28 2.28 0 0 1 0-3.276L12 12z',
    },
    {
        label: 'LangGraph',
        d: 'M5 19H10A5 5 0 115 14ZM19 14A5 5 0 1114 19H19ZM10 5A5 5 0 105 10V5ZM19 5V10A5 5 0 1014 5Z',
    },
] as const;

/* Dynamic class adjusting text size for Cards 1–2 (large) vs Cards 3–5 (small) */
const labelClass = computed(() =>
    props.large
        ? 'm-0 font-normal leading-tight text-[#3d3654] dark:text-white text-base md:text-2xl'
        : 'm-0 font-normal leading-tight text-[#3d3654] dark:text-white text-base md:text-lg'
);

const coreShadow =
    'shadow-[0_0_0_1px_rgba(124,108,246,0.4),0_12px_30px_rgba(90,70,180,0.3)] dark:shadow-[0_0_0_1px_rgba(124,154,255,0.4),0_12px_30px_rgba(0,0,0,0.5)]';
</script>

<template>
    <article
        class="va-show-card relative overflow-hidden rounded-2xl border border-[rgba(124,108,246,0.22)] shadow-[0_20px_45px_-24px_rgba(90,70,180,0.35),inset_0_1px_0_rgba(255,255,255,0.7)] transition-[transform,box-shadow] duration-[350ms] ease-[cubic-bezier(0.25,0.1,0.25,1)] hover:-translate-y-1 hover:shadow-[0_28px_55px_-24px_rgba(90,70,180,0.45),inset_0_1px_0_rgba(255,255,255,0.8)] dark:border-[rgba(96,128,224,0.2)] dark:shadow-[0_24px_50px_-24px_rgba(0,0,0,0.75),inset_0_1px_0_rgba(130,160,255,0.18)] dark:hover:shadow-[0_30px_60px_-24px_rgba(0,0,0,0.85),inset_0_1px_0_rgba(130,160,255,0.25)]"
        :class="[large ? 'aspect-[618/361] lg:col-span-3 va-show-card-grid' : 'aspect-[410/278] lg:col-span-2']">
        <!-- ── 1. Code Generation Visual ── -->
        <div v-if="item.visual === 'codegen'" class="absolute inset-0 z-1" aria-hidden="true">
            <svg class="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 618 361"
                preserveAspectRatio="none" aria-hidden="true" focusable="false">
                <defs>
                    <!-- main connectors feeding into the central chip -->
                    <path id="cg-line-l1" d="M30 120 H150 L172 133 H210" />
                    <path id="cg-line-l2" d="M30 180 H150 L172 167 H210" />
                    <path id="cg-line-r1" d="M588 120 H468 L446 133 H408" />
                    <path id="cg-line-r2" d="M588 180 H468 L446 167 H408" />
                    <!-- faint background circuit traces (HUD density) -->
                    <path id="cg-trace-t" d="M20 46 H180 L200 62 H340 L360 46 H578" />
                    <path id="cg-trace-t2" d="M46 94 H150 L172 80 H300" />
                    <path id="cg-trace-b" d="M28 312 H210 L230 298 H420 L440 312 H590" />
                    <path id="cg-trace-b2" d="M60 330 H190 L210 318 H540" />
                    <linearGradient id="cg-spark-l" x1="-40" y1="0" x2="50" y2="0" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stop-color="#fff" stop-opacity="0" />
                        <stop offset="0.5" stop-color="#fff" stop-opacity="0.95" />
                        <stop offset="1" stop-color="#fff" stop-opacity="0" />
                        <animateTransform attributeName="gradientTransform" type="translate" from="0 0" to="235 0"
                            dur="2.4s" repeatCount="indefinite" />
                    </linearGradient>
                    <linearGradient id="cg-spark-r" x1="568" y1="0" x2="658" y2="0" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stop-color="#fff" stop-opacity="0" />
                        <stop offset="0.5" stop-color="#fff" stop-opacity="0.95" />
                        <stop offset="1" stop-color="#fff" stop-opacity="0" />
                        <animateTransform attributeName="gradientTransform" type="translate" from="0 0" to="-235 0"
                            dur="2.4s" repeatCount="indefinite" />
                    </linearGradient>
                    <linearGradient id="cg-spark-t" x1="-40" y1="0" x2="50" y2="0" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stop-color="#fff" stop-opacity="0" />
                        <stop offset="0.5" stop-color="#fff" stop-opacity="0.8" />
                        <stop offset="1" stop-color="#fff" stop-opacity="0" />
                        <animateTransform attributeName="gradientTransform" type="translate" from="0 0" to="360 0"
                            dur="3s" repeatCount="indefinite" />
                    </linearGradient>
                    <linearGradient id="cg-spark-b" x1="568" y1="0" x2="658" y2="0" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stop-color="#fff" stop-opacity="0" />
                        <stop offset="0.5" stop-color="#fff" stop-opacity="0.8" />
                        <stop offset="1" stop-color="#fff" stop-opacity="0" />
                        <animateTransform attributeName="gradientTransform" type="translate" from="0 0" to="-360 0"
                            dur="3.4s" repeatCount="indefinite" />
                    </linearGradient>
                </defs>
                <!-- static faint traces -->
                <g fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"
                    class="text-[rgba(124,108,246,0.16)] dark:text-[rgba(124,154,255,0.13)]">
                    <use href="#cg-trace-t" />
                    <use href="#cg-trace-t2" />
                    <use href="#cg-trace-b" />
                    <use href="#cg-trace-b2" />
                </g>
                <!-- base connector lines -->
                <g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    class="text-[rgba(124,108,246,0.55)] dark:text-[rgba(124,154,255,0.45)]">
                    <use href="#cg-line-l1" />
                    <use href="#cg-line-l2" />
                    <use href="#cg-line-r1" />
                    <use href="#cg-line-r2" />
                </g>
                <!-- flowing data pulses -->
                <g class="cg-spark-anim drop-shadow-[0_0_3px_rgba(124,108,246,0.5)] dark:drop-shadow-[0_0_3px_rgba(255,255,255,0.7)]"
                    fill="none" stroke-linecap="round">
                    <g stroke-width="3.2" stroke="url(#cg-spark-l)">
                        <use href="#cg-line-l1" />
                        <use href="#cg-line-l2" />
                    </g>
                    <g stroke-width="3.2" stroke="url(#cg-spark-r)">
                        <use href="#cg-line-r1" />
                        <use href="#cg-line-r2" />
                    </g>
                    <use href="#cg-trace-t" stroke-width="2.4" stroke="url(#cg-spark-t)" />
                    <use href="#cg-trace-b" stroke-width="2.4" stroke="url(#cg-spark-b)" />
                </g>
            </svg>
            <span class="cg-beam absolute left-1/2 top-0 h-[30%] w-[3%] -translate-x-1/2 blur-[0.5cqw]"></span>
            <div
                class="cg-chip absolute left-1/2 top-[42%] grid aspect-square w-[33%] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-[4cqw] shadow-[0_0_0_1px_rgba(124,108,246,0.3),0_14px_34px_rgba(90,70,180,0.3),0_0_45px_4px_rgba(124,108,246,0.4),inset_0_2px_8px_rgba(255,255,255,0.6),inset_0_-10px_22px_rgba(124,108,246,0.15)] dark:shadow-[0_0_0_1px_rgba(124,154,255,0.28),0_14px_34px_rgba(0,0,0,0.55),0_0_50px_8px_rgba(124,154,255,0.55),inset_0_2px_8px_rgba(170,195,255,0.4),inset_0_-10px_22px_rgba(0,0,24,0.55)]">
                <span
                    class="cg-ring absolute aspect-square w-[56%] rounded-full border-[0.55cqw] border-[rgba(124,108,246,0.85)] shadow-[0_0_9px_2px_rgba(124,108,246,0.6),inset_0_0_9px_rgba(124,108,246,0.4)] dark:border-[rgba(150,182,255,0.95)] dark:shadow-[0_0_9px_2px_rgba(120,160,255,0.75),inset_0_0_9px_rgba(120,160,255,0.55)]"></span>
                <span
                    class="relative z-10 text-center text-[3.5cqw] font-semibold leading-[1.05] text-[#4c2a8a] dark:text-white">AI<br />99%</span>
            </div>
            <span v-for="(pos, i) in cgNodes" :key="i"
                class="cg-node absolute h-[1.8cqw] w-[1.8cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#7c6cf0] shadow-[0_0_7px_2px_rgba(124,108,246,0.9)] dark:bg-white dark:shadow-[0_0_7px_2px_rgba(170,195,255,0.95)]"
                :class="pos"></span>
            <span v-for="(t, i) in cgHudTicks" :key="i"
                class="hud-tick pointer-events-none absolute z-10 whitespace-nowrap font-mono tracking-[0.2em] select-none"
                :class="[t.cls, t.pos]" :style="{ animationDelay: `${i * 0.45}s` }">{{ t.text }}</span>
        </div>

        <!-- ── 2. Checklist Visual ── -->
        <div v-else-if="item.visual === 'checklist'" class="absolute inset-0 z-1" aria-hidden="true">
            <svg class="ck-spark pointer-events-none absolute inset-0 h-full w-full drop-shadow-[0_0_3px_rgba(124,108,246,0.4)] dark:drop-shadow-[0_0_3px_rgba(255,255,255,0.5)]"
                viewBox="0 0 618 361" preserveAspectRatio="none" aria-hidden="true" focusable="false">
                <defs>
                    <linearGradient id="ck-spark-l" x1="-80" y1="0" x2="20" y2="0" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stop-color="#fff" stop-opacity="0" />
                        <stop offset="0.5" stop-color="#fff" stop-opacity="0.95" />
                        <stop offset="1" stop-color="#fff" stop-opacity="0" />
                        <animateTransform attributeName="gradientTransform" type="translate" from="0 0" to="350 0"
                            dur="2.4s" repeatCount="indefinite" />
                    </linearGradient>
                    <linearGradient id="ck-spark-r" x1="618" y1="0" x2="718" y2="0" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stop-color="#fff" stop-opacity="0" />
                        <stop offset="0.5" stop-color="#fff" stop-opacity="0.95" />
                        <stop offset="1" stop-color="#fff" stop-opacity="0" />
                        <animateTransform attributeName="gradientTransform" type="translate" from="0 0" to="-250 0"
                            dur="2.4s" repeatCount="indefinite" />
                    </linearGradient>
                </defs>

                <g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    class="text-[rgba(124,108,246,0.45)] dark:text-[rgba(124,154,255,0.35)]">
                    <path d="M160 110 H200 C220 110, 230 162, 250 162 H280" />
                    <path d="M160 214 H200 C220 214, 230 162, 250 162 H280" />
                    <path d="M120 162 H280" />

                    <path d="M618 110 H590 C570 110, 560 162, 545 162 H520" />
                    <path d="M618 214 H590 C570 214, 560 162, 545 162 H520" />
                    <path d="M618 162 H520" />
                </g>

                <g fill="none" stroke-width="2.2" stroke-linecap="round">
                    <g stroke="url(#ck-spark-l)">
                        <path d="M160 110 H200 C220 110, 230 162, 250 162 H280" />
                        <path d="M160 214 H200 C220 214, 230 162, 250 162 H280" />
                        <path d="M120 162 H280" />
                    </g>
                    <g stroke="url(#ck-spark-r)">
                        <path d="M618 110 H590 C570 110, 560 162, 545 162 H520" />
                        <path d="M618 214 H590 C570 214, 560 162, 545 162 H520" />
                        <path d="M618 162 H520" />
                    </g>
                </g>
            </svg>

            <span
                class="absolute left-[45.3%] top-[44.87%] z-10 h-[7cqw] w-[7cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(49.66%_49.66%_at_50%_50.34%,#90B3FF_0%,rgba(102,136,209,0)_100%)]"></span>
            <span
                class="absolute left-[45.3%] top-[44.87%] z-10 h-[2cqw] w-[2cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#7c6cf0] dark:bg-white"></span>
            <span
                class="absolute left-[84.1%] top-[44.87%] z-10 h-[7cqw] w-[7cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(49.66%_49.66%_at_50%_50.34%,#90B3FF_0%,rgba(102,136,209,0)_100%)]"></span>
            <span
                class="absolute left-[84.1%] top-[44.87%] z-10 h-[2cqw] w-[2cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#7c6cf0] dark:bg-white"></span>

            <pre
                class="pointer-events-none absolute left-[5%] top-[18%] m-0 w-[36%] select-none font-mono text-[1.6cqw] leading-[1.6] text-[rgba(84,78,150,0.85)] whitespace-pre dark:text-[rgba(176,188,255,0.72)]"><code><span class="text-[#c026d3] dark:text-[rgba(224,112,192,0.85)]">pdvfcw wfumm</span> KocweMiln ;
      [arr] &lt;= 1:
    <span class="text-[#c026d3] dark:text-[rgba(224,112,192,0.85)]">sreturn</span> arr

pivot = arr[0]
left = [x <span class="text-[#c026d3] dark:text-[rgba(224,112,192,0.85)]">for</span> x in arr[1:]
<span class="text-[#c026d3] dark:text-[rgba(224,112,192,0.85)]">if</span> x &lt; pivot]
right = [x <span class="text-[#c026d3] dark:text-[rgba(224,112,192,0.85)]">for</span> x in arr[1:]
<span class="text-[#c026d3] dark:text-[rgba(224,112,192,0.85)]">if</span> x &gt;= pivot]

<span class="text-[#c026d3] dark:text-[rgba(224,112,192,0.85)]">return</span> quick_sort(left) +
[pivot] + quick_sort(right)</code></pre>

            <div
                class="absolute left-[64.5%] top-[38%] flex w-[36%] -translate-x-1/2 -translate-y-1/2 flex-col items-center">
                <span
                    class="mb-[1.8cqw] block text-[3.6cqw] font-bold tracking-[-0.01em] text-[#3d3654] drop-shadow-[0_1px_2px_rgba(124,108,246,0.3)] dark:text-white dark:drop-shadow-[0_0_10px_rgba(180,200,255,0.9)]">
                    VaahAgents
                </span>

                <div class="flex w-full flex-col gap-[1.1cqw]">
                    <div v-for="n in 3" :key="n"
                        class="relative flex items-center gap-[1.2cqw] rounded-[1.4cqw] border border-[rgba(124,108,246,0.3)] px-[2cqw] py-[2.4cqw] shadow-[0_6px_16px_rgba(90,70,180,0.25)] dark:border-[rgba(124,154,255,0.3)] dark:shadow-[0_6px_16px_rgba(0,0,0,0.45)]">
                        <i
                            class="h-[1.6cqw] w-[1.6cqw] shrink-0 rounded-full bg-[#f45b8f] shadow-[0_0_6px_rgba(244,91,143,0.85)]"></i>
                        <i
                            class="h-[1.6cqw] w-[1.6cqw] shrink-0 rounded-full bg-[#3b66ff] shadow-[0_0_5px_rgba(59,102,255,0.7)]"></i>
                        <i
                            class="h-[1.6cqw] w-[1.6cqw] shrink-0 rounded-full bg-[#f45b8f] shadow-[0_0_6px_rgba(244,91,143,0.85)]"></i>
                        <span
                            class="h-[0.8cqw] flex-1 rounded-full bg-[#3b66ff]/80 shadow-[inset_0_1px_2px_rgba(0,0,0,0.3)]"></span>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── 3. Scaffolding Visual ── -->
        <div v-else-if="item.visual === 'scaffold'" class="absolute inset-0 z-1" aria-hidden="true">
            <pre
                class="absolute left-[8%] top-[12%] m-0 w-[84%] rounded-[2.5cqw] border border-[rgba(124,108,246,0.18)] bg-[rgba(255,255,255,0.5)] p-[3cqw_3.5cqw] font-mono text-[2.1cqw] leading-[1.9] text-[rgba(70,64,140,0.9)] whitespace-pre dark:border-[rgba(96,128,224,0.18)] dark:bg-[rgba(10,16,44,0.4)] dark:text-[rgba(150,168,255,0.8)]"><span class="text-[rgba(124,108,246,0.7)] dark:text-[rgba(120,140,220,0.55)]">// place the frame in the current window</span>
<span>window::Current-&gt;Content = rootFrame;</span>
<span>Window::</span>
<span class="text-[rgba(124,108,246,0.7)] dark:text-[rgba(120,140,220,0.55)]">//Ensure</span>
<span>Window::</span>
<span>}, task_co</span>
<span>}</span>
<span>else</span></pre>
            <div
                class="absolute left-[30%] top-[27%] min-w-[34cqw] rounded-[2cqw] border border-[rgba(124,108,246,0.3)] bg-[rgba(255,255,255,0.9)] p-[1.5cqw] shadow-[0_16px_34px_rgba(90,70,180,0.25)] dark:border-[rgba(124,154,255,0.3)] dark:bg-[rgba(9,12,36,0.94)] dark:shadow-[0_16px_34px_rgba(0,0,0,0.5)]">
                <div v-for="row in scaffoldRows" :key="row.label"
                    class="flex items-center gap-[1.6cqw] rounded-[1cqw] p-[1.2cqw_1.6cqw] text-[2cqw]"
                    :class="[row.active ? 'bg-[rgba(124,108,246,0.15)] text-[#4c2a8a] dark:bg-[rgba(91,127,255,0.2)] dark:text-white' : 'text-[#5b54a0] dark:text-[#cfd6ff]', row.dim ? 'opacity-50' : '']">
                    <WrIcon :name="row.icon" size="14" />{{ row.label }}
                </div>
            </div>
        </div>

        <!-- ── 4. Code Review & Audit Visual ── -->
        <div v-else-if="item.visual === 'review'" class="absolute inset-0 z-1" aria-hidden="true">
            <svg class="pointer-events-none absolute inset-0 h-full w-full drop-shadow-[0_0_8px_rgba(124,108,246,0.35)] dark:drop-shadow-[0_0_8px_rgba(96,128,224,0.4)]"
                viewBox="0 0 618 361" aria-hidden="true" focusable="false">
                <path d="M 260 -13 A 165 165 0 0 0 260 317" fill="none" stroke="currentColor" stroke-width="1.8"
                    stroke-linecap="round" class="text-[rgba(124,108,246,0.4)] dark:text-[rgba(124,154,255,0.3)]" />
                <path d="M 260 44 A 108 108 0 0 0 260 260" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" class="text-[rgba(124,108,246,0.55)] dark:text-[rgba(150,180,255,0.45)]" />
            </svg>

            <span v-for="(ic, i) in reviewIcons" :key="i"
                class="absolute grid aspect-square w-6 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-[rgba(124,108,246,0.4)] bg-[rgba(255,255,255,0.95)] text-[#5b54a0] shadow-[0_4px_14px_rgba(90,70,180,0.25)] dark:border-[rgba(124,154,255,0.4)] dark:bg-[rgba(12,18,48,0.95)] dark:text-[#cfd6ff] dark:shadow-[0_4px_14px_rgba(0,0,0,0.6)] z-10"
                :class="ic.pos">
                <WrIcon :name="ic.icon" size="16" />
            </span>

            <span v-for="(pos, i) in reviewDots" :key="i"
                class="absolute h-[1.8cqw] w-[1.8cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#7c6cf0] shadow-[0_0_12px_4px_rgba(124,108,246,0.9)] dark:bg-white dark:shadow-[0_0_12px_4px_rgba(200,225,255,0.95)] z-10"
                :class="pos"></span>

            <div class="va-show-core review-core absolute left-[42%] top-[42%] z-20 grid aspect-square w-[20cqw] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full"
                :class="coreShadow">
                <WrSVGIcon name="WrAiIcon" class="h-[8cqw] w-[8cqw] text-white" />
            </div>

            <!-- Extended code snippet with bottom mask blur + fade effect -->
            <pre
                class="absolute right-[10%] top-[10%] m-0 w-[42%] h-[72%] rounded-[2cqw] border border-[rgba(124,108,246,0.2)] bg-[rgba(255,255,255,0.55)] p-[2.5cqw_3cqw] font-mono text-[1.7cqw] leading-[1.75] text-[rgba(70,64,140,0.9)] whitespace-pre overflow-hidden [mask-image:linear-gradient(180deg,#000_0%,#000_50%,transparent_98%)] [-webkit-mask-image:linear-gradient(180deg,#000_0%,#000_50%,transparent_98%)] dark:border-[rgba(96,128,224,0.2)] dark:bg-[rgba(10,16,44,0.5)] dark:text-[rgba(150,168,255,0.85)]"><span>1. {</span>
<span>2. "product": {</span>
<span>3.    }</span>
<span>4.    "product_name":</span>
<span>5.    "price" : $48.00</span>
<span>6.    },</span>
<span>7.    {</span>
<span class="opacity-70 blur-[0.2px]">8.    "product_name":</span>
<span class="opacity-40 blur-[0.6px]">9.    "price" : $48.00</span>
<span class="opacity-20 blur-[1px]">10.   }</span></pre>
        </div>

        <!-- ── 5. Integration Visual ── -->
        <div v-else class="absolute inset-0 z-1" aria-hidden="true">
            <div class="absolute left-[8%] right-[8%] top-[8%] grid h-[60%] grid-cols-6 gap-[2cqw]">
                <span v-for="agent in agentIcons" :key="agent.label"
                    class="grid place-items-center rounded-[1.4cqw] border border-[rgba(124,108,246,0.14)] bg-[rgba(255,255,255,0.5)] dark:border-[rgba(124,154,255,0.14)] dark:bg-[rgba(30,44,104,0.35)]">
                    <svg viewBox="0 0 24 24"
                        class="h-[6cqw] w-[6cqw] fill-current text-[#5b54a0] opacity-40 blur-[0.3cqw] dark:text-[#cfd6ff]"
                        aria-hidden="true" focusable="false">
                        <path :d="agent.d" />
                    </svg>
                </span>
            </div>

            <span
                class="int-wave-outer pointer-events-none absolute left-1/2 top-[40%] aspect-square w-[46cqw] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[rgba(124,108,246,0.22)] bg-[radial-gradient(circle,rgba(124,108,246,0.18)_0%,rgba(124,108,246,0.05)_65%,transparent_100%)] shadow-[0_0_35px_rgba(124,108,246,0.25)] dark:border-[rgba(140,165,255,0.22)] dark:bg-[radial-gradient(circle,rgba(40,70,190,0.18)_0%,rgba(15,25,80,0.05)_65%,transparent_100%)] dark:shadow-[0_0_35px_rgba(59,130,246,0.25)]"></span>
            <span
                class="int-wave-mid pointer-events-none absolute left-1/2 top-[40%] aspect-square w-[32cqw] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[rgba(124,108,246,0.32)] bg-[radial-gradient(circle,rgba(124,108,246,0.28)_0%,rgba(148,120,240,0.1)_70%,transparent_100%)] shadow-[0_0_24px_rgba(124,108,246,0.4),inset_0_0_16px_rgba(124,108,246,0.2)] dark:border-[rgba(150,180,255,0.32)] dark:bg-[radial-gradient(circle,rgba(60,95,230,0.28)_0%,rgba(20,35,110,0.1)_70%,transparent_100%)] dark:shadow-[0_0_24px_rgba(66,98,214,0.4),inset_0_0_16px_rgba(59,130,246,0.2)]"></span>
            <span
                class="int-core-halo pointer-events-none absolute left-1/2 top-[40%] z-10 aspect-square w-[26cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(124,108,246,0.5)_0%,rgba(124,108,246,0)_68%)] blur-[7cqw] dark:bg-[radial-gradient(circle,rgba(60,95,230,0.55)_0%,rgba(60,95,230,0)_68%)]"></span>

            <div
                class="va-show-core absolute left-1/2 top-[40%] z-20 grid aspect-square w-[18cqw] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full shadow-[0_0_30px_10px_rgba(124,108,246,0.5),0_12px_32px_rgba(90,70,180,0.35)] dark:shadow-[0_0_30px_10px_rgba(30,60,180,0.6),0_12px_32px_rgba(0,0,0,0.7)]">
                <WrSVGIcon name="WrAiIcon"
                    class="h-[8cqw] w-[8cqw] text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
            </div>
        </div>

        <!-- ── Title & Subtitle Labels ── -->
        <div class="absolute left-[5.5%] bottom-[5%] z-30 max-w-[82%]">
            <h3 :class="labelClass">
                {{ item.title }}
            </h3>
            <p v-if="item.subtitle" :class="labelClass">
                {{ item.subtitle }}
            </p>
        </div>
    </article>
</template>

<style scoped>
.va-show-card {
    container-type: inline-size;
    --va-pulse: rgba(124, 108, 246, 0.35);
    --va-pulse-strong: rgba(124, 108, 246, 0.7);
    background:
        radial-gradient(130% 85% at 50% -18%, rgba(124, 108, 246, 0.16) 0%, rgba(124, 108, 246, 0) 55%),
        linear-gradient(180deg, #ffffff 0%, #f6f4ff 42%, #f1edff 74%, #faf8ff 100%);
}

html.my-app-dark .va-show-card {
    --va-pulse: rgba(59, 130, 246, 0.35);
    --va-pulse-strong: rgba(59, 130, 246, 0.7);
    background:
        radial-gradient(130% 85% at 50% -18%, rgba(66, 98, 214, 0.5) 0%, rgba(24, 36, 96, 0) 55%),
        linear-gradient(180deg, #10204f 0%, #0a1338 42%, #070c28 74%, #05081d 100%);
}

/* Blue ambient glow: large cards only */
.va-show-card-grid {
    background-image:
        radial-gradient(49.66% 49.66% at 50% 50.34%, rgba(144, 179, 255, 0.12) 0%, rgba(102, 136, 209, 0) 100%),
        linear-gradient(180deg, #ffffff 0%, #f6f4ff 42%, #f1edff 74%, #faf8ff 100%);
}

html.my-app-dark .va-show-card-grid {
    background-image:
        radial-gradient(49.66% 49.66% at 50% 50.34%, rgba(144, 179, 255, 0.1) 0%, rgba(102, 136, 209, 0) 100%),
        linear-gradient(180deg, #10204f 0%, #0a1338 42%, #070c28 74%, #05081d 100%);
}

/* Grid texture: large cards only */
.va-show-card-grid::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    background-image:
        linear-gradient(rgba(124, 108, 246, 0.1) 1px, transparent 1px),
        linear-gradient(90deg, rgba(124, 108, 246, 0.1) 1px, transparent 1px);
    background-size: 9cqw 9cqw;
    -webkit-mask-image: radial-gradient(120% 100% at 50% 42%, #000 50%, transparent 100%);
    mask-image: radial-gradient(120% 100% at 50% 42%, #000 50%, transparent 100%);
    pointer-events: none;
}

html.my-app-dark .va-show-card-grid::before {
    background-image:
        linear-gradient(rgba(96, 128, 224, 0.08) 1px, transparent 1px),
        linear-gradient(90deg, rgba(96, 128, 224, 0.08) 1px, transparent 1px);
}

.va-show-card::after {
    content: '';
    position: absolute;
    top: -1px;
    left: 10%;
    right: 10%;
    height: 2px;
    z-index: 4;
    background: linear-gradient(90deg,
            transparent,
            rgba(124, 108, 246, 0.9) 25%,
            rgba(215, 205, 255, 1) 50%,
            rgba(124, 108, 246, 0.9) 75%,
            transparent);
    box-shadow: 0 0 16px 3px rgba(124, 108, 246, 0.45);
    pointer-events: none;
}

html.my-app-dark .va-show-card::after {
    background: linear-gradient(90deg,
            transparent,
            rgba(150, 190, 255, 0.95) 25%,
            rgba(215, 232, 255, 1) 50%,
            rgba(150, 190, 255, 0.95) 75%,
            transparent);
    box-shadow: 0 0 16px 3px rgba(96, 140, 255, 0.55);
}

.cg-chip {
    background: linear-gradient(155deg, #eef0ff 0%, #dfe3ff 52%, #d3d8ff 100%);
}

html.my-app-dark .cg-chip {
    background: linear-gradient(155deg, #31479a 0%, #1d2f6b 52%, #14204d 100%);
}

.cg-ring {
    animation: va-card-pulse 2.8s ease-in-out infinite;
}

.cg-node {
    animation: va-node-pulse 2.4s ease-in-out infinite;
}

.hud-tick {
    color: rgba(124, 108, 246, 0.5);
    text-shadow: 0 0 6px rgba(124, 108, 246, 0.35);
    animation: va-hud-twinkle 2.8s ease-in-out infinite;
}

.hud-tick--ai {
    color: rgba(124, 108, 246, 0.85);
    text-shadow: 0 0 7px rgba(124, 108, 246, 0.5);
}

html.my-app-dark .hud-tick {
    color: rgba(180, 200, 255, 0.55);
    text-shadow: 0 0 6px rgba(150, 180, 255, 0.5);
}

html.my-app-dark .hud-tick--ai {
    color: rgba(220, 232, 255, 0.9);
    text-shadow: 0 0 7px rgba(160, 190, 255, 0.6);
}

@keyframes va-node-pulse {

    0%,
    100% {
        opacity: 0.7;
    }

    50% {
        opacity: 1;
    }
}

@keyframes va-hud-twinkle {

    0%,
    100% {
        opacity: 0.2;
    }

    50% {
        opacity: 0.85;
    }
}

.cg-beam {
    background: linear-gradient(180deg, rgba(124, 108, 246, 0.9), rgba(124, 108, 246, 0.1));
}

html.my-app-dark .cg-beam {
    background: linear-gradient(180deg, rgba(190, 210, 255, 0.95), rgba(120, 150, 255, 0.12));
}

.va-show-core {
    background: radial-gradient(circle at 40% 35%, #7c8cf0 0%, #5b6cd8 60%, #4753dc 100%);
    animation: va-card-pulse 3s ease-in-out infinite;
}

html.my-app-dark .va-show-core {
    background: radial-gradient(circle at 40% 35%, #3a54c8 0%, #1f3188 60%, #16245f 100%);
}

/* Review card core: no pulsing glow */
.review-core {
    animation: none;
}

.int-wave-outer {
    animation: va-ring-pulse 4s ease-in-out infinite alternate;
}

.int-wave-mid {
    animation: va-ring-pulse 3s ease-in-out infinite alternate-reverse;
}

@keyframes va-ring-pulse {
    0% {
        transform: translate(-50%, -50%) scale(0.96);
        opacity: 0.7;
    }

    100% {
        transform: translate(-50%, -50%) scale(1.04);
        opacity: 1;
    }
}

@keyframes va-card-pulse {

    0%,
    100% {
        filter: drop-shadow(0 0 6px var(--va-pulse));
    }

    50% {
        filter: drop-shadow(0 0 14px var(--va-pulse-strong));
    }
}

@media (prefers-reduced-motion: reduce) {
    .va-show-card {
        transition: none;
    }

    .va-show-card:hover {
        transform: none;
    }

    .cg-ring,
    .cg-node,
    .hud-tick,
    .va-show-core,
    .int-wave-outer,
    .int-wave-mid {
        animation: none;
    }

    .cg-spark-anim,
    .ck-spark {
        display: none;
    }
}
</style>